/**
 * Prominent card showing the proposed solution (markdown) and verification
 * output (monospace terminal-style block). Populated by /moftaki-fix or
 * manually by an admin.
 *
 * The "Verification" section (typically `## Verification` in the markdown)
 * is the highest-signal part of the solution for the submitter — it's how
 * they decide whether to confirm or reject. We pull it out of the prose and
 * render it as a standalone green callout at the top of the card so it's
 * the first thing the eye lands on.
 */
import { Box, Chip, Paper, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';

interface Props {
  solutionSummary: string;
  testProof: string | null;
  proposedBy: string | null;
  proposedAt: string | null;
}

/**
 * Split the markdown so the Verification section can be rendered separately.
 * Matches `## Verification` (any heading level >= h2) up to the next `##` or
 * EOF. If the section isn't found the original markdown is returned untouched
 * — older tickets without a Verification heading still render fine.
 */
function splitVerification(md: string): { body: string; verification: string | null } {
  const re = /\n(#{2,4})\s+Verification\s*\n([\s\S]*?)(?=\n#{1,4}\s|$)/i;
  const match = md.match(re);
  if (!match || match.index === undefined) return { body: md, verification: null };
  const verification = match[2].trim();
  const body = (md.slice(0, match.index) + md.slice(match.index + match[0].length)).trim();
  return { body, verification: verification || null };
}

export const ProposedSolutionCard = ({ solutionSummary, testProof, proposedBy, proposedAt }: Props) => {
  const theme = useTheme();
  const { body, verification } = splitVerification(solutionSummary);
  const successMain = theme.palette.success.main;

  // Shared markdown styling block for both the main body and the verification
  // callout. Defined once so the two render with consistent typography.
  const markdownSx = {
    '& p': { my: 0.75 },
    '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.75, fontWeight: 600 },
    '& h1': { fontSize: '1.05rem' },
    '& h2': { fontSize: '0.95rem' },
    '& h3': { fontSize: '0.88rem' },
    '& ul, & ol': { my: 0.5, pl: 2.5 },
    '& li': { mb: 0.25 },
    '& code': { bgcolor: alpha(theme.palette.secondary.main, 0.08), px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: '0.8em', fontFamily: 'monospace' },
    '& pre': { bgcolor: alpha(theme.palette.secondary.main, 0.06), p: 1.5, borderRadius: 1, overflow: 'auto', fontSize: '0.8rem' },
    '& pre code': { bgcolor: 'transparent', p: 0 },
    '& blockquote': { borderLeft: `3px solid ${alpha(theme.palette.secondary.main, 0.4)}`, pl: 1.5, my: 1, color: 'text.secondary' },
    '& a': { color: 'secondary.main' },
    fontSize: '0.88rem', lineHeight: 1.6,
  } as const;

  return (
    <Paper variant="outlined" sx={{
      p: 2.5, mb: 2.5,
      bgcolor: alpha(theme.palette.secondary.main, 0.04),
      borderColor: alpha(theme.palette.secondary.main, 0.3),
      borderLeftWidth: 4, borderLeftColor: theme.palette.secondary.main,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
          Proposed Solution
        </Typography>
        {proposedBy && (
          <Chip label={proposedBy} size="small" variant="outlined"
            sx={{ fontSize: '0.65rem', height: 18, color: 'secondary.main', borderColor: alpha(theme.palette.secondary.main, 0.4) }} />
        )}
        {proposedAt && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
            {new Date(proposedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      {/* Verification callout — pulled out of the markdown body and rendered
          at the very top of the card. This is what the submitter needs to
          read to decide whether to confirm or reject, so it gets the strongest
          visual weight on the page. */}
      {verification && (
        <Box sx={{
          mb: 2, mt: 0.5, p: 1.75, pl: 2,
          borderRadius: 1.5,
          bgcolor: alpha(successMain, 0.08),
          border: `1px solid ${alpha(successMain, 0.35)}`,
          borderLeft: `5px solid ${successMain}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <CheckCircleOutline sx={{ fontSize: 18, color: successMain }} />
            <Typography variant="subtitle2" sx={{
              fontWeight: 800, color: successMain,
              fontSize: '0.85rem', letterSpacing: '0.02em', textTransform: 'uppercase',
            }}>
              Verification
            </Typography>
          </Box>
          <Box sx={{ ...markdownSx, '& li': { mb: 0.4 }, fontSize: '0.9rem', color: 'text.primary' }}>
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            >
              {verification}
            </ReactMarkdown>
          </Box>
        </Box>
      )}

      <Box sx={markdownSx}>
        <ReactMarkdown
          components={{
            // All links (e.g. "Test at" URLs) open in a new tab so the user
            // stays on the feedback detail page.
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
          }}
        >
          {body}
        </ReactMarkdown>
      </Box>

      {testProof && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Verification Output
          </Typography>
          <Box component="pre" sx={{
            m: 0, p: 1.5, borderRadius: 1,
            bgcolor: alpha('#000', 0.85), color: '#d4d4d4',
            fontSize: '0.7rem', fontFamily: 'monospace',
            maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>{testProof}</Box>
        </Box>
      )}
    </Paper>
  );
};
